/**
 * Feedback — shared toast + confirm-dialog primitives.
 *
 * Codex audit B-10 + B-11 fix in one provider:
 *
 *   - **B-10 (toast):** `useToast()` returns a `notify(message, opts?)`
 *     function. Toasts stack in the bottom-right; auto-dismiss after
 *     `duration` (default 4s); manual-dismissable via X.
 *
 *   - **B-11 (confirm):** `useConfirm()` returns an async `confirm({title, message, danger?})`
 *     that resolves true/false. Replaces blocking `window.confirm()`
 *     (which freezes the event loop, breaks DnD drop reset, and
 *     looks like 1998).
 *
 * Both surfaces live in one provider so the app mounts ONE pair of
 * portals at the root. Multiple toasts can coexist; only one confirm
 * dialog can be open at a time.
 *
 * The confirm/prompt hosts render through `createPortal(…, document.body)`
 * at z-[210] — above ModalShell (200) and SlideOver (120) — so a confirm
 * invoked from inside a modal is never trapped behind it (see the
 * `backdrop-filter` containing-block trap documented in ModalShell.tsx).
 *
 * Mounted in main.tsx around the router, so any descendant can call
 * `useToast()` / `useConfirm()`.
 */

import { createContext, useContext } from 'react'

// ─── Toast ────────────────────────────────────────────────────────────────

export type ToastTone = 'success' | 'info' | 'error'

export type ToastOptions = {
  tone?: ToastTone
  /** Auto-dismiss after this many ms. 0 = stays until user closes. */
  durationMs?: number
  /** Optional "action" link rendered to the right of the message. */
  action?: { label: string; onClick: () => void }
  /**
   * This IS the plan-gate toast (PlanGateToaster), so it is never dropped as
   * the echo of a 402 the shell just surfaced (G-38c, lib/plan-gate.ts).
   */
  fromPlanGate?: boolean
}

export type ToastEntry = {
  id: string
  message: string
  tone: ToastTone
  action?: { label: string; onClick: () => void }
}

export type ToastContextValue = {
  notify: (message: string, opts?: ToastOptions) => void
}

export const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <FeedbackProvider>')
  return ctx
}

// ─── Confirm ──────────────────────────────────────────────────────────────

export type ConfirmOptions = {
  title: string
  message: string
  /** Confirm-button label. Defaults to "Confirm" (or "Delete" when danger). */
  confirmLabel?: string
  cancelLabel?: string
  /** Red destructive styling on the confirm button. */
  danger?: boolean
}

export type ConfirmContextValue = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>
}

export const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used inside <FeedbackProvider>')
  return ctx
}

// ─── Prompt ───────────────────────────────────────────────────────────────

export type PromptOptions = {
  title: string
  /** Helper copy shown under the title. */
  message?: string
  /** Pre-fills the input. */
  defaultValue?: string
  placeholder?: string
  /** Confirm-button label. Defaults to "OK". */
  confirmLabel?: string
  cancelLabel?: string
  /** Render as a textarea instead of a single-line input. */
  multiline?: boolean
  /** Required — submit disabled when empty/whitespace. */
  required?: boolean
  /** Synchronous validator; return null for "ok", or an error string. */
  validate?: (value: string) => string | null
}

export type PromptContextValue = {
  prompt: (opts: PromptOptions) => Promise<string | null>
}

export const PromptContext = createContext<PromptContextValue | null>(null)

export function usePrompt(): PromptContextValue {
  const ctx = useContext(PromptContext)
  if (!ctx) throw new Error('usePrompt must be used inside <FeedbackProvider>')
  return ctx
}
